import { getAuthHeaders } from '@/utils/authFetch';
import type {
  CentralChatContext,
  CentralChatMessage,
  ChatHubConversation,
  ChatHubConversationDetail,
  ChatHubStreamEvent,
  ClientToolResult,
  PageToolDescriptor,
} from '@/components/CentralChat/types';
import {
  parseTaProposalReceipt,
  type TaApprovalOutcome,
} from '@/components/CentralChat/taProposalTypes';

const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '/api/v1').replace(/\/$/, '');

function chatHubUrl(path: string): string {
  return `${API_BASE_URL}${path}`;
}

export class ChatHubRequestError extends Error {
  readonly status: number;

  constructor(status: number, detail: string) {
    super(`Chat hub request failed (${status}): ${detail}`);
    this.name = 'ChatHubRequestError';
    this.status = status;
  }
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const text = await response.text();
    throw new ChatHubRequestError(response.status, text);
  }
  return response.json() as Promise<T>;
}

export interface ChatHubStreamRequest {
  message: string;
  tenant_id: string;
  chat_mode?: 'ask' | 'full_access';
  conversation_id?: string | null;
  context: CentralChatContext;
  conversation_history: Array<{ role: 'user' | 'assistant'; content: string }>;
  model?: string;
  provider?: string;
  enable_web_research?: boolean;
  enable_image_generation?: boolean;
  enable_seo_analysis?: boolean;
  /**
   * What the page the user is looking at can run. Sent on every turn because
   * the answer changes with the page; absent on every browser without the
   * agent surface, where the backend then renders no page-tools block at all.
   */
  page_tools?: readonly PageToolDescriptor[];
  /**
   * A page tool's answer, resuming the turn that parked waiting for it. The
   * only request that may carry an empty `message`: it continues a turn the
   * user already started rather than starting one.
   */
  client_tool_result?: ClientToolResult;
}

export interface StreamChatHubHandlers {
  onEvent: (event: ChatHubStreamEvent) => void;
  signal?: AbortSignal;
}

export interface TechnicalAuditIssuePreparationRequest {
  tenant_id: string;
  issue_id: string;
}

export type TechnicalAuditIssuePreparation =
  | {
      status: 'ready';
      issue_id: string;
      preparation_id: string;
      expires_at: string;
    }
  | {
      status: 'blocked' | 'failed';
      issue_id: string;
      reason: string;
    };

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export async function prepareTechnicalAuditIssueChat(
  request: TechnicalAuditIssuePreparationRequest,
  signal?: AbortSignal,
): Promise<TechnicalAuditIssuePreparation> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(
    chatHubUrl(
      `/chat-hub/technical-audit/issues/${encodeURIComponent(request.issue_id)}/prepare`,
    ),
    {
      method: 'POST',
      headers: { ...authHeaders, 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenant_id: request.tenant_id }),
      signal,
    },
  );
  const data = await parseJsonResponse<unknown>(response);
  if (!isRecord(data) || data.issue_id !== request.issue_id) {
    throw new Error('Technical Audit preparation did not match the requested issue.');
  }
  if (
    data.status === 'ready' &&
    typeof data.preparation_id === 'string' &&
    data.preparation_id &&
    typeof data.expires_at === 'string' &&
    Number.isFinite(Date.parse(data.expires_at))
  ) {
    return {
      status: 'ready',
      issue_id: data.issue_id,
      preparation_id: data.preparation_id,
      expires_at: data.expires_at,
    };
  }
  if (data.status === 'blocked' || data.status === 'failed') {
    return {
      status: data.status,
      issue_id: data.issue_id,
      reason:
        typeof data.reason === 'string' && data.reason.trim()
          ? data.reason
          : 'The page could not be prepared for this chat.',
    };
  }
  throw new Error('Technical Audit preparation returned an unsupported response.');
}

export function isChatHubPreparationConflict(error: unknown): boolean {
  return error instanceof ChatHubRequestError && error.status === 409;
}

export function bindTechnicalAuditIssuePreparation(
  context: CentralChatContext,
  preparation: Extract<TechnicalAuditIssuePreparation, { status: 'ready' }>,
): CentralChatContext {
  if (
    context.context_type !== 'technical_audit_issue' ||
    context.issue_id !== preparation.issue_id
  ) {
    throw new Error('Technical Audit preparation cannot be attached to a different issue.');
  }
  return {
    ...context,
    payload: {
      ...context.payload,
      technical_audit_preparation_id: preparation.preparation_id,
    },
  };
}

export function extractSSEEvents(chunk: string): ChatHubStreamEvent[] {
  const events: ChatHubStreamEvent[] = [];
  for (const block of chunk.split('\n\n')) {
    const dataLines = block
      .split('\n')
      .filter((line) => line.startsWith('data: '))
      .map((line) => line.slice(6));
    if (!dataLines.length) continue;
    try {
      events.push(JSON.parse(dataLines.join('\n')) as ChatHubStreamEvent);
    } catch (error) {
      console.warn('[chatHubService] Failed to parse SSE block', block, error);
    }
  }
  return events;
}

export async function streamChatHub(
  request: ChatHubStreamRequest,
  handlers: StreamChatHubHandlers,
): Promise<void> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(chatHubUrl('/chat-hub/stream'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...authHeaders,
    },
    body: JSON.stringify(request),
    signal: handlers.signal,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ChatHubRequestError(response.status, text);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error('Chat hub stream is not readable');

  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const lastBoundary = buffer.lastIndexOf('\n\n');
    if (lastBoundary === -1) continue;

    const ready = buffer.slice(0, lastBoundary + 2);
    buffer = buffer.slice(lastBoundary + 2);
    for (const event of extractSSEEvents(ready)) {
      handlers.onEvent(event);
    }
  }

  if (buffer.trim()) {
    for (const event of extractSSEEvents(`${buffer}\n\n`)) {
      handlers.onEvent(event);
    }
  }
}

export async function listChatHubConversations(
  tenantId: string,
): Promise<ChatHubConversation[]> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(
    chatHubUrl(`/chat-hub/conversations?tenant_id=${encodeURIComponent(tenantId)}`),
    { headers: authHeaders },
  );
  return parseJsonResponse<ChatHubConversation[]>(response);
}

export async function getChatHubConversation(
  conversationId: string,
): Promise<ChatHubConversationDetail> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(
    chatHubUrl(`/chat-hub/conversations/${encodeURIComponent(conversationId)}`),
    { headers: authHeaders },
  );
  return parseJsonResponse<ChatHubConversationDetail>(response);
}

export async function deleteChatHubConversation(
  conversationId: string,
): Promise<void> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(
    chatHubUrl(`/chat-hub/conversations/${encodeURIComponent(conversationId)}`),
    {
      method: 'DELETE',
      headers: authHeaders,
    },
  );
  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Chat hub request failed (${response.status}): ${text}`);
  }
}

/**
 * Tell Eve to stop the turn running for this conversation. Fire and forget:
 * the local abort has already ended the read, and both of Eve's outcomes are
 * success, so a failure here is logged, never surfaced — the 150s turn cap
 * remains the backstop on the spend.
 */
export async function settleChatHubApproval(
  conversationId: string,
  requestId: string,
  decision: 'approve' | 'cancel',
  tenantId: string,
  handlers: StreamChatHubHandlers,
): Promise<void> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(
    chatHubUrl(
      `/chat-hub/conversations/${encodeURIComponent(conversationId)}/approvals/${encodeURIComponent(requestId)}`,
    ),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...authHeaders,
      },
      body: JSON.stringify({ tenant_id: tenantId, decision }),
      signal: handlers.signal,
    },
  );

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Chat hub request failed (${response.status}): ${text}`);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error('Chat hub stream is not readable');

  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lastBoundary = buffer.lastIndexOf('\n\n');
    if (lastBoundary === -1) continue;
    const ready = buffer.slice(0, lastBoundary + 2);
    buffer = buffer.slice(lastBoundary + 2);
    for (const event of extractSSEEvents(ready)) {
      handlers.onEvent(event);
    }
  }
  if (buffer.trim()) {
    for (const event of extractSSEEvents(`${buffer}\n\n`)) {
      handlers.onEvent(event);
    }
  }
}

/** Answer Emjae's question (ask_question) and read the reply's stream, like settling an approval. */
export async function answerChatHubQuestion(
  conversationId: string,
  requestId: string,
  answer: { optionId: string } | { text: string },
  tenantId: string,
  handlers: StreamChatHubHandlers,
): Promise<void> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(
    chatHubUrl(
      `/chat-hub/conversations/${encodeURIComponent(conversationId)}/answers/${encodeURIComponent(requestId)}`,
    ),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream', ...authHeaders },
      body: JSON.stringify(
        'optionId' in answer
          ? { tenant_id: tenantId, option_id: answer.optionId }
          : { tenant_id: tenantId, text: answer.text },
      ),
      signal: handlers.signal,
    },
  );

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Chat hub request failed (${response.status}): ${text}`);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error('Chat hub stream is not readable');

  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lastBoundary = buffer.lastIndexOf('\n\n');
    if (lastBoundary === -1) continue;
    const ready = buffer.slice(0, lastBoundary + 2);
    buffer = buffer.slice(lastBoundary + 2);
    for (const event of extractSSEEvents(ready)) handlers.onEvent(event);
  }
  if (buffer.trim()) {
    for (const event of extractSSEEvents(`${buffer}\n\n`)) handlers.onEvent(event);
  }
}

export interface TechnicalAuditApprovalRequest {
  conversation_id: string;
  tenant_id: string;
  proposal_id: string;
  approval_request_id: string;
  decision: 'approve' | 'decline';
}

const UNRESOLVED_SETTLEMENT = new Set([
  'Settlement is still pending.',
  'Settlement is unknown.',
  'Hold changed during settlement.',
]);

const approvalFailureReason = (status: number, payload: unknown, text: string): string => {
  if (payload && typeof payload === 'object' && 'detail' in payload) {
    const detail = (payload as { detail: unknown }).detail;
    if (typeof detail === 'string' && detail.trim()) return detail.trim();
  }
  const trimmed = text.trim();
  return trimmed || `Chat hub request failed (${status})`;
};

const unresolvedSettlement = (body: Record<string, unknown>): boolean => {
  const status = body.status;
  if (status === 'pending' || status === 'unknown') return true;
  return typeof body.reason === 'string' && UNRESOLVED_SETTLEMENT.has(body.reason);
};

export async function settleTechnicalAuditApproval(
  request: TechnicalAuditApprovalRequest,
  signal?: AbortSignal,
): Promise<TaApprovalOutcome> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(
    chatHubUrl(
      `/chat-hub/conversations/${encodeURIComponent(request.conversation_id)}/ta-approvals`,
    ),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...authHeaders,
      },
      body: JSON.stringify({
        tenant_id: request.tenant_id,
        proposal_id: request.proposal_id,
        approval_request_id: request.approval_request_id,
        decision: request.decision,
      }),
      signal,
    },
  );
  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = null;
    }
  }
  if (response.status >= 500 || response.status === 408) {
    return { ok: true, pending: true };
  }
  if (!response.ok) {
    return { ok: false, reason: approvalFailureReason(response.status, payload, text) };
  }
  if (!payload || typeof payload !== 'object') {
    return { ok: true, pending: true };
  }
  const body = payload as Record<string, unknown>;
  if (unresolvedSettlement(body)) {
    return { ok: true, pending: true };
  }
  if (body.ok === false) {
    return {
      ok: false,
      reason:
        typeof body.reason === 'string' && body.reason.trim()
          ? body.reason.trim()
          : 'The held write could not be settled.',
    };
  }
  if (body.ok !== true) {
    return { ok: true, pending: true };
  }
  const receipt = parseTaProposalReceipt(body.receipt, request.proposal_id);
  if (!receipt) {
    return { ok: true, pending: true };
  }
  return { ok: true, receipt };
}

export async function cancelChatHubTurn(
  conversationId: string,
  tenantId: string,
): Promise<void> {
  try {
    const authHeaders = await getAuthHeaders();
    await fetch(
      chatHubUrl(`/chat-hub/conversations/${encodeURIComponent(conversationId)}/cancel`),
      {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenant_id: tenantId }),
      },
    );
  } catch (error) {
    console.warn('[central-chat] cancel did not reach the backend', error);
  }
}

export function compactHistory(messages: CentralChatMessage[]) {
  return messages
    .filter((message) => message.content.trim())
    .slice(-20)
    .map((message) => ({
      role: message.role,
      content: message.content,
    }));
}
